import React, { useCallback, useState } from 'preact/compat';

import { AssistantMessage } from './AssistantMessage';
import CircuitIcon from './CircuitIcon';
import CopyButton from './CopyButton';
import { useAutoScroll } from './hooks/useAutoScroll';
import {
  makeBotTextMessage,
  makeWelcomeMessage,
  useChatHistory,
} from './hooks/useChatHistory';
import { useChatStream } from './hooks/useChatStream';
import MaximizeIcon from './MaximizeIcon';
import MinimizeIcon from './MinimizeIcon';
import SendIcon from './SendIcon';
import { Message } from './types/message.types';
import XIcon from './XIcon';

const SESSION_RESET_NOTICE = makeBotTextMessage(
  'Your session expired, so we started a new conversation.',
);

export type WidgetPosition =
  | 'bottom-right'
  | 'bottom-left'
  | 'top-right'
  | 'top-left';

interface ChatWidgetProps {
  apiKey: string;
  bubbleColor?: string;
  position?: WidgetPosition;
  greeting?: string;
  defaultOpen?: boolean;
}

const ChatWidget: React.FC<ChatWidgetProps> = ({
  apiKey,
  bubbleColor,
  position = 'bottom-right',
  greeting,
  defaultOpen = false,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);

  const { isLoadingHistory } = useChatHistory({
    apiKey,
    isOpen,
    setMessages,
    greeting,
  });
  const handleSessionReset = useCallback(() => {
    setMessages([SESSION_RESET_NOTICE, makeWelcomeMessage(greeting)]);
  }, [greeting]);
  const { input, setInput, isLoading, progressText, handleSend } =
    useChatStream({ apiKey, setMessages, onSessionReset: handleSessionReset });
  const messagesEndRef = useAutoScroll({ messages, isOpen, isLoadingHistory });

  const positionClass =
    position !== 'bottom-right' ? `position-${position}` : '';

  return (
    <div className={`circuit-chat-root ${positionClass}`}>
      <button
        className={`chat-bubble ${isOpen ? 'closed' : 'open'}`}
        onClick={() => setIsOpen(true)}
        style={bubbleColor ? { backgroundColor: bubbleColor } : undefined}
      >
        <CircuitIcon
          width={32}
          height={32}
        />
      </button>

      {isOpen && isExpanded && (
        <div
          className='chat-overlay'
          onClick={() => setIsExpanded(false)}
        />
      )}

      <div
        className={`chat-widget ${isOpen ? 'open' : 'closed'}${isExpanded ? ' expanded' : ''}`}
      >
        <div className='chat-header'>
          <span>Chat with our Assistant</span>
          <div className='chat-header-actions'>
            <button
              className='expand-button'
              onClick={() => setIsExpanded((prev) => !prev)}
            >
              {isExpanded ? (
                <MinimizeIcon size={16} />
              ) : (
                <MaximizeIcon size={16} />
              )}
            </button>
            <button
              className='close-button'
              onClick={() => {
                setIsOpen(false);
                setIsExpanded(false);
              }}
            >
              <XIcon size={20} />
            </button>
          </div>
        </div>
        <div className='chat-messages'>
          {isLoadingHistory ? (
            <div className='history-loading'>
              <div className='history-loading-spinner' />
            </div>
          ) : (
            <>
              {messages.map((msg, index) =>
                msg.sender === 'user' ? (
                  <div
                    key={index}
                    className='message-row user-row'
                  >
                    <div className={`message ${msg.sender}`}>{msg.text}</div>
                    <div className='user-message-actions'>
                      <CopyButton text={msg.text} />
                    </div>
                  </div>
                ) : (
                  <AssistantMessage
                    key={index}
                    events={msg.events}
                    isStreaming={isLoading && index === messages.length - 1}
                  />
                ),
              )}
              {progressText && (
                <div className='progress-text'>{progressText}</div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>
        <form
          className='chat-input-container'
          onSubmit={(e) => {
            e.preventDefault();
            handleSend();
          }}
        >
          <input
            type='text'
            className='chat-input'
            value={input}
            onChange={(e) => setInput((e.target as HTMLInputElement).value)}
            placeholder='Type your message...'
            disabled={isLoading || isLoadingHistory}
          />
          <button
            type='submit'
            className='send-button'
            disabled={isLoading || isLoadingHistory || !input.trim()}
          >
            <SendIcon size={18} />
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatWidget;
